import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Avatar,
  Chip,
} from '@mui/material';
import {
  CheckCircle,
  Email,
  Login,
  Dashboard,
  Work,
  Group,
  EmojiEvents,
  MusicNote,
  Visibility,
  Edit,
  CloudUpload,
  Search,
} from '@mui/icons-material';

export const EngineerSignupSuccess: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0A0E1A',
        backgroundImage: `
          radial-gradient(ellipse at top right, rgba(76, 175, 80, 0.1) 0%, transparent 50%),
          radial-gradient(ellipse at bottom left, rgba(255, 107, 53, 0.1) 0%, transparent 50%)
        `,
        padding: 2,
      }}
    >
      <Card sx={{ 
        maxWidth: 700, 
        width: '100%',
        background: 'rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              mb: 3,
              padding: 3,
              borderRadius: '50%',
              background: 'rgba(76, 175, 80, 0.1)',
              border: '2px solid rgba(76, 175, 80, 0.3)',
            }}>
              <EmojiEvents sx={{ fontSize: 60, color: 'success.main' }} />
            </Box>
            
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
              Welcome to the Community!
            </Typography>
            
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Your engineer profile has been created successfully
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
              <Chip 
                icon={<MusicNote />} 
                label="Audio Engineer" 
                color="primary" 
                variant="filled" 
              />
              <Chip 
                icon={<CheckCircle />} 
                label="Account Created" 
                color="success" 
                variant="filled" 
              />
            </Box>
          </Box>

          <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Dashboard sx={{ mr: 1 }} />
              What's next?
            </Typography>
            
            <List>
              <ListItem>
                <ListItemIcon>
                  <Email color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Verify your email"
                  secondary="Check your inbox and click the verification link to activate your account"
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <Edit color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Complete your profile"
                  secondary="Add more details, upload a professional photo, and showcase your experience"
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <CloudUpload color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Upload portfolio samples"
                  secondary="Add audio demos, previous work, and showcase your skills to potential clients"
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <Search color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Explore opportunities"
                  secondary="Browse studios, connect with other engineers, and find collaboration opportunities"
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <Visibility color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Get discovered"
                  secondary="Studios can now find and invite you to projects based on your skills and experience"
                />
              </ListItem>
            </List>
          </Paper>

          <Paper sx={{ p: 3, mb: 3, bgcolor: 'rgba(255, 107, 53, 0.05)', border: '1px solid rgba(255, 107, 53, 0.2)' }}>
            <Typography variant="h6" gutterBottom color="primary">
              Pro Tips for Success
            </Typography>
            
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 30 }}>
                  <CheckCircle color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Keep your availability status updated"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon sx={{ minWidth: 30 }}>
                  <CheckCircle color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Upload high-quality audio samples that showcase your best work"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon sx={{ minWidth: 30 }}>
                  <CheckCircle color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Be specific about your specializations and genres"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon sx={{ minWidth: 30 }}>
                  <CheckCircle color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Connect with other professionals to grow your network"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            </List>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/login')}
              startIcon={<Login />}
              sx={{
                background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
                boxShadow: '0 4px 20px rgba(255, 107, 53, 0.3)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 24px rgba(255, 107, 53, 0.4)',
                },
              }}
            >
              Sign In to Your Account
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              onClick={() => window.open('https://docs.abydosone.com/engineers', '_blank')}
            >
              Engineer Guide
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Need help getting started?{' '}
              <Button
                variant="text"
                size="small"
                sx={{ color: 'primary.main' }}
                onClick={() => window.open('mailto:support@abydosone.com', '_blank')}
              >
                Contact Support
              </Button>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};